import { TranslationsState, initialState } from './translations.state';
import { createReducer, on } from '@ngrx/store';
import {
  createTranslation,
  createTranslationFailure,
  createTranslationSuccess,
  deleteTranslation,
  deleteTranslationFailure,
  deleteTranslationSuccess,
  loadTranslations,
  loadTranslationsFailure,
  loadTranslationsSuccess,
  resetLastTranslationKey,
  updateTranslation,
  updateTranslationFailure,
  updateTranslationSuccess,
} from './translations.actions';
import { ITranslationsRequest } from '../translations.interfaces';

export const translationsReducer = createReducer(
  initialState,
  on(loadTranslations, (state) => {
    if (Object.keys(state.translations).length > 0) {
      return state;
    }
    return {
      ...state,
      loading: true,
      error: null,
    };
  }),
  on(loadTranslationsSuccess, (state, { translations, dsTreeItems }) => ({
    ...state,
    translations: { ...translations },
    dsTreeItems,
    loading: false,
    error: null,
  })),
  on(loadTranslationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(createTranslation, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(createTranslationSuccess, (state, { translation }) =>
    updateState(state, translation)
  ),
  on(createTranslationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(updateTranslation, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(updateTranslationSuccess, (state, { translation }) =>
    updateState(state, translation)
  ),
  on(updateTranslationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(deleteTranslation, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(deleteTranslationSuccess, (state, { translation }) =>
    removeTranslation(state, translation)
  ),
  on(deleteTranslationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(resetLastTranslationKey, (state) => ({
    ...state,
    lastTranslationKey: undefined,
    lastTranslationNamespace: undefined,
  }))
);

const updateState = (
  state: TranslationsState,
  translation: ITranslationsRequest
): TranslationsState => {
  const { locale, namespace, key, value } = translation;

  const existingLocale = state.translations[locale] || {};
  const existingNamespace = existingLocale[namespace] || {};

  return {
    ...state,
    lastTranslationKey: key,
    lastTranslationNamespace: namespace,
    translations: {
      ...state.translations,
      [locale]: {
        ...existingLocale,
        [namespace]: {
          ...existingNamespace,
          [key]: value,
        },
      },
    },
    loading: false,
    error: null,
  };
};

const removeTranslation = (
  state: TranslationsState,
  translation: ITranslationsRequest
): TranslationsState => {
  const { locale, namespace, key } = translation;
  const newTranslations = { ...state.translations };

  if (newTranslations[locale]?.[namespace]) {
    const updatedNamespace = { ...newTranslations[locale][namespace] };
    delete updatedNamespace[key];

    if (Object.keys(updatedNamespace).length === 0) {
      delete newTranslations[locale][namespace];
    }

    if (Object.keys(newTranslations[locale]).length === 0) {
      delete newTranslations[locale];
    }
  }

  return {
    ...state,
    translations: newTranslations,
    loading: false,
    error: null,
  };
};
